import { useTheme } from '@emotion/react';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { ChangeEvent, FC, ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { OPTIONS_PLACEHOLDER } from '../../constants/Dropdown';
import {
  Container,
  DropdownWrapper,
  ErrorText,
  IconWrapper,
  LabelWrapper,
  StyledDropdown,
  StyledLabel,
} from './Dropdown.styles';
import { DropdownProps } from './Dropdown.types';

const Dropdown: FC<DropdownProps> = forwardRef(
  (
    {
      id,
      disabled,
      label,
      onChange,
      options,
      optionsPlaceholder = 'Select',
      value,
      hidePlaceholder = false,
      errorText,
      dataTestId = 'Dropdown',
      ...props
    },
    ref: ForwardedRef<HTMLSelectElement>
  ) => {
    const theme = useTheme();
    const [selectedValue, setSelectedValue] = useState<string | undefined>(value || OPTIONS_PLACEHOLDER);

    const optionsList = options?.map((option) => {
      return (
        <option key={id + option.value} value={option.value} data-testid={'selectOption'}>
          {option.label}
        </option>
      );
    });

    useEffect(() => {
      setSelectedValue(value);
    }, [value]);

    const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
      if (onChange) {
        onChange(event);
      }
      setSelectedValue(event.target.value);
    };

    return (
      <Container>
        <LabelWrapper>
          <StyledLabel htmlFor={id}>{label}</StyledLabel>
        </LabelWrapper>
        <DropdownWrapper>
          <StyledDropdown
            {...props}
            aria-label={label}
            ref={ref}
            id={id}
            value={selectedValue}
            disabled={disabled}
            onChange={handleChange}
            data-testid={dataTestId}
          >
            {!hidePlaceholder && (
              <option value={OPTIONS_PLACEHOLDER} disabled>
                {optionsPlaceholder}
              </option>
            )}
            {optionsList}
          </StyledDropdown>
          <IconWrapper>
            <KeyboardArrowDownOutlinedIcon htmlColor={disabled ? theme.colors.gray400 : theme.colors.gray900} />
          </IconWrapper>
        </DropdownWrapper>
        {errorText ? <ErrorText aria-live='assertive'>{errorText}</ErrorText> : <></>}
      </Container>
    );
  }
);

export default Dropdown;
